import { memo, ReactNode } from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Tag } from "@design-system/Tag/Tag";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { NestedEditorRow } from "../nestedRowTypes";
import { NestedEditorTableMeta } from "../nestedTableMeta";
import { EditorInlineEditField } from "../../cells/EditorInlineEditField";
import sharedStyles from "../../ChartOfAccountsTemplateEditor.module.scss";
import styles from "../ChartOfAccountsTemplateNestedEditor.module.scss";

export const NestedNameCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, NestedEditorRow, unknown>) => {
        const item = row.original;
        const meta = table.options.meta as NestedEditorTableMeta | undefined;

        const capabilities = item.account
            ? meta?.getRowCapabilities(item.account)
            : undefined;
        const placeholder = capabilities?.placeholder ?? null;
        const renameBlockReason = capabilities?.renameBlockReason ?? null;

        const isHeading = item.kind === "type" || item.kind === "section";
        const isGroupRow = item.kind === "group" && item.group != null;
        // Rename-editable when the row is an account the host does not lock and
        // that is not a placeholder.
        const isLocked = meta?.isEditingDisabled === true;
        const isRenameEditable =
            item.account != null &&
            placeholder == null &&
            renameBlockReason == null &&
            !isLocked;
        const isArchived = item.account?.isDisabled ?? false;

        const archivedTag: ReactNode = isArchived ? (
            <Tag color="neutral" size="sm">
                Archived
            </Tag>
        ) : undefined;

        const resolveNameNode = (): ReactNode => {
            // Custom group: inline-renameable. A blank name shows the yellow
            // "Add name" affordance (flagWhenEmpty); a duplicate name in scope
            // shows the red error styling (hasError).
            if (item.kind === "group" && item.group) {
                const group = item.group;
                return (
                    <span
                        className={sharedStyles.editorCellAnchor}
                        data-editor-cell={`${item.id}:name`}
                    >
                        <EditorInlineEditField
                            value={group.name}
                            valueClassName={styles.headingText}
                            addText="Add name"
                            flagWhenEmpty
                            readonly={isLocked}
                            hasError={item.groups.some(
                                (g) =>
                                    meta?.duplicateGroupNameIds?.has(g.id) ??
                                    false,
                            )}
                            onCommit={(name) =>
                                meta?.callbacks?.onRenameGroup(group.id, name)
                            }
                            onBlankExit={
                                meta?.pendingDraftIds?.has(group.id)
                                    ? () =>
                                          meta?.callbacks?.onCancelPendingAdd(
                                              group.id,
                                          )
                                    : undefined
                            }
                        />
                    </span>
                );
            }
            // Placeholder account: blue static text behind an info tooltip.
            if (item.account && placeholder) {
                return (
                    <span className={sharedStyles.staticCellText}>
                        <span className={sharedStyles.inlineValueWrapper}>
                            <Tooltip content={placeholder.tooltip}>
                                <span className={sharedStyles.variableText}>
                                    {item.name}
                                </span>
                            </Tooltip>
                            {archivedTag}
                        </span>
                    </span>
                );
            }
            // Rename-editable account: inline-editable name with an Archived addon.
            if (isRenameEditable && item.account) {
                const account = item.account;
                return (
                    <span
                        className={sharedStyles.editorCellAnchor}
                        data-editor-cell={`${item.id}:name`}
                    >
                        <EditorInlineEditField
                            value={account.name}
                            addText="Add name"
                            flagWhenEmpty
                            hasError={item.accounts.some(
                                (a) =>
                                    meta?.duplicateNameIds?.has(a.id) ?? false,
                            )}
                            readonly={isLocked}
                            onCommit={(name) => {
                                const role = capabilities?.role;
                                if (role != null) {
                                    meta?.callbacks?.onRequestRenameAccount({
                                        id: account.id,
                                        name,
                                        role,
                                    });
                                    return;
                                }
                                meta?.callbacks?.onUpdateAccount(account.id, {
                                    name,
                                });
                            }}
                            onBlankExit={
                                meta?.pendingDraftIds?.has(account.id)
                                    ? () =>
                                          meta?.callbacks?.onCancelPendingAdd(
                                              account.id,
                                          )
                                    : undefined
                            }
                        />
                        {archivedTag}
                    </span>
                );
            }
            // Rename-locked account + structural rows: static text. The
            // `staticCellText` wrapper aligns it with the editable name fields.
            return (
                <span className={sharedStyles.staticCellText}>
                    <span className={sharedStyles.inlineValueWrapper}>
                        <span>{item.name}</span>
                        {archivedTag}
                    </span>
                </span>
            );
        };

        const isEditable = !isLocked && (isRenameEditable || isGroupRow);

        return (
            <ViewTableCell captureClicks={isEditable}>
                <span className={styles.nameCellRow}>
                    <span
                        className={classNames(styles.nameContent, {
                            [styles.headingText]: isHeading || isGroupRow,
                        })}
                    >
                        {resolveNameNode()}
                    </span>
                </span>
            </ViewTableCell>
        );
    },
);

NestedNameCell.displayName = "NestedNameCell";
